// Universal AI summary card. Mounted today only by the matter page's
// AISummary, which always passes its own summary, so the derivations below
// run on no screen yet.
//
// One component, five contexts: matter, client, judge, opposing counsel,
// firm. Each context resolves an `AISummary` from the data
// already on hand (matter has it first-class; the others derive from
// cross-matter signals via `derive.ts`). When derivation has nothing to
// say, the card renders a friendly empty state pointing at what would
// unlock a real summary.
//
// Only a summary wears the AI marker: the loading, error and empty states
// are sentences the app wrote, so they sit in a plain card.
//
// The card never throws: a thrown derivation surfaces as a small
// error banner inside the card. Loading is opt-in through the
// `loading` prop because most contexts derive synchronously from
// already-fetched data.

import { useMemo, useState } from "react";
import type { AISummary, Matter } from "../../data/types";
import { SEP } from "../chrome/SepList";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import { fmtDate } from "../../lib/dates";
import {
  deriveClientSummary,
  deriveFirmSummary,
  deriveJudgeSummary,
  deriveOpposingCounselSummary,
} from "./derive";

const AI_DRAFT = `AI draft${SEP}attorney review`;
const GENERATE_HINT = "No summary yet. Generate one from the matter's record and its documents.";

export type AICardContext = "matter" | "client" | "judge" | "opposing" | "firm";

interface BaseProps {
  context: AICardContext;
  id: string;
  title?: string;
  loading?: boolean;
  /** Optional override so callers that already have the matter loaded
      (e.g. MatterDashboard) don't trigger a fixture re-lookup. */
  matterOverride?: Matter | null;
  /** Optional pre-derived summary - skips the built-in derivation. */
  summaryOverride?: AISummary | null;
  emptyMessageOverride?: string;
  /** For judge / opposing contexts the matter list is fetched async by
      the parent; we don't refetch here. */
  contextMatters?: Matter[];
  onGenerate?: () => void;
  generating?: boolean;
  generateError?: string | null;
}

interface Props extends BaseProps {}

interface Resolved {
  summary: AISummary | null;
  emptyMessage?: string;
  sourceMatterCount?: number;
}

function resolveSummary(
  context: AICardContext,
  id: string,
  opts: {
    matterOverride?: Matter | null;
    summaryOverride?: AISummary | null;
    emptyMessageOverride?: string;
    contextMatters?: Matter[];
  },
): Resolved {
  if (opts.summaryOverride !== undefined) {
    return { summary: opts.summaryOverride, emptyMessage: opts.emptyMessageOverride };
  }

  switch (context) {
    case "matter": {
      const m = opts.matterOverride;
      if (hasSummary(m?.aiSummary)) {
        return { summary: m.aiSummary };
      }
      return { summary: null };
    }
    case "client":
      return deriveClientSummary(id);
    case "judge":
      return deriveJudgeSummary({ judgeId: id, matters: opts.contextMatters ?? [] });
    case "opposing":
      return deriveOpposingCounselSummary({
        counselId: id,
        matters: opts.contextMatters ?? [],
      });
    case "firm":
      return deriveFirmSummary();
  }
}

function defaultEmptyMessage(context: AICardContext): string {
  switch (context) {
    case "matter":
      return "AI summary will appear once this matter is analyzed.";
    case "client":
      return "AI summary will appear once one of this client's matters is analyzed.";
    case "judge":
      return "AI summary will appear once this judge has 3+ matters in the firm.";
    case "opposing":
      return "AI summary will appear once this counsel has 3+ matters in the firm.";
    case "firm":
      return "AI summary will appear once the firm has at least one active matter.";
  }
}

function defaultTitle(context: AICardContext): string {
  switch (context) {
    case "matter":
      return "AI summary";
    case "client":
      return `AI summary${SEP}across this client's matters`;
    case "judge":
      return `AI profile${SEP}judge`;
    case "opposing":
      return `AI profile${SEP}opposing counsel`;
    case "firm":
      return `AI summary${SEP}firm-wide`;
  }
}

export function hasSummary(s: AISummary | null | undefined): s is AISummary {
  return !!(s?.text || s?.headline);
}

// The server asks the model for plain text: UPPERCASE headers ending in a
// colon, sections apart by a blank line. The walker folds every paragraph
// under the header above it and takes a bare header on any line; a header
// with text beside it counts only where a paragraph starts, and never as
// the first line under a bare header, where "A-NUMBER: ..." is body.
const SECTION_HEAD = /^([A-Z][^a-z:]{2,}):\s*(.*)$/;

interface SummarySection {
  title?: string;
  paragraphs: string[];
}

function sectionsOf(text: string): SummarySection[] {
  const sections: SummarySection[] = [];
  let lines: string[] = [];
  const flush = () => {
    const paragraph = lines.join("\n").trim();
    lines = [];
    if (!paragraph) return;
    if (sections.length === 0) sections.push({ paragraphs: [] });
    sections[sections.length - 1]!.paragraphs.push(paragraph);
  };
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    const head = SECTION_HEAD.exec(line);
    const open = sections[sections.length - 1];
    if (head && (!head[2] || (lines.length === 0 && (!open || open.paragraphs.length > 0)))) {
      flush();
      sections.push({ title: head[1], paragraphs: [] });
      if (head[2]) lines.push(head[2]);
    } else if (!line) {
      flush();
    } else {
      lines.push(line);
    }
  }
  flush();
  return sections;
}

const SECTIONS_SHOWN = 2;

function confidenceBucket(c: number | undefined | null): "high" | "med" | "low" | null {
  if (c == null) return null;
  if (c >= 0.75) return "high";
  if (c >= 0.5) return "med";
  return "low";
}

export function AICard(props: Props) {
  const {
    context,
    id,
    title,
    loading = false,
    matterOverride,
    summaryOverride,
    emptyMessageOverride,
    contextMatters,
    onGenerate,
    generating = false,
    generateError,
  } = props;

  const [expanded, setExpanded] = useState(false);
  const resolved = useMemo<Resolved & { error?: string }>(() => {
    try {
      return resolveSummary(context, id, {
        matterOverride,
        summaryOverride,
        emptyMessageOverride,
        contextMatters,
      });
    } catch (e) {
      return {
        summary: null,
        emptyMessage: undefined,
        error: e instanceof Error ? e.message : "AI summary derivation failed",
      };
    }
  }, [context, id, matterOverride, summaryOverride, emptyMessageOverride, contextMatters]);

  const headTitle = title ?? defaultTitle(context);
  const summary = resolved.summary;

  // The control look whatever the card wears: the chip says who wrote the
  // words, and the button is the firm's.
  const generateButton = onGenerate && (
    <button
      type="button"
      className="aicard-refresh"
      disabled={generating}
      onClick={onGenerate}
      title={summary ? "Write this summary again from the matter's current data" : undefined}
    >
      {generating ? "Generating..." : summary ? "Refresh" : "Generate summary"}
    </button>
  );
  const errorLine = generateError && <p className="aicard-error">{generateError}</p>;
  const skeleton = (label: string) => (
    <SkeletonGroup label={label}>
      <Skeleton width="55%" />
      <Skeleton width="92%" />
      <Skeleton width="78%" />
    </SkeletonGroup>
  );

  if (loading) {
    return (
      <div className="aicard cc-card" aria-busy="true">
        <div className="aicard-head">
          <span className="aicard-title">{headTitle}</span>
        </div>
        {skeleton("Loading AI summary")}
      </div>
    );
  }

  if (resolved.error) {
    return (
      <div className="aicard cc-card">
        <div className="aicard-head">
          <span className="aicard-title">{headTitle}</span>
        </div>
        <p className="aicard-error">{resolved.error}</p>
      </div>
    );
  }

  if (!summary) {
    const emptyMessage =
      resolved.emptyMessage ?? (onGenerate ? GENERATE_HINT : defaultEmptyMessage(context));
    return (
      <div className="aicard cc-card" aria-busy={generating || undefined}>
        <div className="aicard-head">
          <span className="aicard-title">{headTitle}</span>
          {generateButton && <div className="aicard-actions">{generateButton}</div>}
        </div>
        {generating ? (
          skeleton("Generating AI summary")
        ) : (
          <p className="aicard-empty">{emptyMessage}</p>
        )}
        {errorLine}
      </div>
    );
  }

  const pct = summary.confidence != null ? Math.round(summary.confidence * 100) : null;
  const bucket = confidenceBucket(summary.confidence);

  const cols: Array<{ title: string; items?: string[] }> = [
    { title: "Strengths", items: summary.strengths },
    { title: "Risks", items: summary.risks },
    { title: "Next-best actions", items: summary.nextBestActions },
  ];
  const filled = cols.filter((c) => c.items && c.items.length > 0);
  const sections = summary.text ? sectionsOf(summary.text) : [];
  const folded = !expanded && sections.length > SECTIONS_SHOWN;

  return (
    <>
      <div className="aicard ai-panel" aria-busy={generating || undefined}>
        <div className="aicard-head">
          <span className="aicard-title">{headTitle}</span>
          <span className="ai-chip">{AI_DRAFT}</span>
          {(bucket || generateButton) && (
            <div className="aicard-actions">
              {bucket && pct != null && (
                <span className="aicard-confidence" title={`Confidence: ${bucket} (${pct}%)`}>
                  <span className={`aicard-confidence-dot ${bucket}`} aria-hidden />
                  <span>Confidence</span>
                  <span className="aicard-confidence-pct">{pct}%</span>
                </span>
              )}
              {generateButton}
            </div>
          )}
        </div>

        {summary.text ? (
          <div className="aicard-sections">
            {(folded ? sections.slice(0, SECTIONS_SHOWN) : sections).map((s, i) => (
              <section key={i} className="aicard-section">
                {s.title && <div className="aicard-section-title">{s.title}</div>}
                {s.paragraphs.map((text, j) => (
                  <p key={j} className="aicard-text">
                    {text}
                  </p>
                ))}
              </section>
            ))}
          </div>
        ) : (
          <>
            <p className="aicard-headline">{summary.headline}</p>
            {filled.length > 0 && (
              <div
                className="aicard-grid"
                style={{ ["--aicard-cols" as string]: String(Math.min(3, filled.length)) }}
              >
                {filled.map((c) => (
                  <div key={c.title} className="aicard-col">
                    <div className="aicard-col-title">{c.title}</div>
                    <ul>
                      {(c.items ?? []).map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="aicard-foot">
          {generating ? (
            <Skeleton width={180} height={12} />
          ) : (
            <span>
              {summary.generatedAt ? (
                <>
                  Generated <span className="tnum">{fmtDate(summary.generatedAt)}</span>
                </>
              ) : resolved.sourceMatterCount != null ? (
                `Derived from ${resolved.sourceMatterCount} matter${resolved.sourceMatterCount === 1 ? "" : "s"}`
              ) : (
                "Auto-generated"
              )}
            </span>
          )}
          {sections.length > SECTIONS_SHOWN && (
            <button
              type="button"
              className="aicard-toggle"
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
            >
              {folded ? `Show all ${sections.length} sections` : "Show less"}
            </button>
          )}
        </div>
      </div>
      {errorLine && <div className="aicard cc-card">{errorLine}</div>}
    </>
  );
}
